import { createFileRoute, Link } from "@tanstack/react-router";
import { CTABand } from "@/components/site/CTABand";
import { PhoneButton } from "@/components/site/PhoneButton";
import { BUSINESS } from "@/lib/business";
import reviewsHero from "@/assets/reviews-hero.webp";

const DESC =
  "See customer reviews for Phoenix Plumbing Co. from Phoenix homeowners and businesses on our water heater, drain, leak, and emergency plumbing service.";

export const Route = createFileRoute("/reviews")({
  head: () => ({
    meta: [
      { title: "Phoenix Plumbing Reviews | Phoenix Plumbing Co." },
      { name: "description", content: DESC },
      { property: "og:title", content: "Phoenix Plumbing Reviews | Phoenix Plumbing Co." },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://www.phoenixplumbing.co/reviews" },
      { name: "twitter:title", content: "Phoenix Plumbing Reviews | Phoenix Plumbing Co." },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://www.phoenixplumbing.co/reviews" }],
  }),
  component: Page,
});

function Page() {
  const googleReviewsUrl = `https://www.google.com/maps?cid=${BUSINESS.cid}`;
  return (
    <>
      <section className="relative bg-secondary text-secondary-foreground overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: `url(${reviewsHero})` }} />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-secondary/50" />
        <div className="relative mx-auto max-w-4xl px-4 py-14">
          <h1 className="text-4xl md:text-5xl font-extrabold">Phoenix Plumbing Reviews</h1>
          <p className="mt-4 text-secondary-foreground/90 text-lg">
            Phoenix Plumbing Co. works hard to provide honest plumbing service, clear
            communication, and quality work for homeowners and businesses in Phoenix, AZ.
            Customer feedback helps new customers understand what to expect when they call our team.
          </p>
          <div className="mt-6"><PhoneButton size="lg" /></div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-12 prose prose-lg">
        <h2>What Customers Often Mention</h2>
        <p>
          Customers often choose Phoenix Plumbing Co. because they want a plumber who
          communicates clearly, shows up ready to work, explains the problem, and gives practical
          repair options. We help with water heaters, drain cleaning, leaks, sewer lines, toilets,
          faucets, garbage disposals, and emergency plumbing service.
        </p>

        <h2>Common Plumbing Jobs We Handle</h2>
        <ul>
          <li>Water heater repairs</li>
          <li>Drain clogs</li>
          <li>Leak detection</li>
          <li>Toilet repairs</li>
          <li>Faucet and fixture repairs</li>
          <li>Sewer line problems</li>
          <li>Garbage disposal issues</li>
          <li>Plumbing inspections</li>
          <li>Emergency plumbing calls</li>
        </ul>

        <h2>Leave Phoenix Plumbing Co. a Review</h2>
        <p>
          If Phoenix Plumbing Co. helped with your plumbing issue, we appreciate your feedback.
          Reviews help local homeowners and businesses find a plumber they can trust.
        </p>
        <p>
          We don't show a star rating here because ratings change and screenshots go stale — the
          buttons below take you to our live Google Business Profile, where every review is
          unfiltered and current.
        </p>
      </article>

      <section className="mx-auto max-w-3xl px-4 pb-12">
        <div className="flex flex-wrap gap-3 justify-center">
          <a href={googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-6 py-3 font-extrabold hover:opacity-90">
            Read Our Reviews
          </a>
          <a href={BUSINESS.reviewLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md border-2 border-primary text-primary px-6 py-3 font-extrabold hover:bg-primary/10">
            Leave a Review
          </a>
          <Link to="/contact" className="inline-flex items-center justify-center rounded-md border-2 border-secondary text-secondary px-6 py-3 font-extrabold hover:bg-secondary/10">
            Contact Phoenix Plumbing Co.
          </Link>
        </div>
      </section>

      <CTABand />
    </>
  );
}
